import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Calendar, Download, Eye, Truck, Filter, Package, FileText, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const OutwardReports = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('all-entries');
  
  // All Entries Tab State
  const [entries, setEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [transferType, setTransferType] = useState('all');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);

  // Item Wise Summary Tab State
  const [entriesForSummary, setEntriesForSummary] = useState([]);
  const [selectedEntryId, setSelectedEntryId] = useState('');
  const [itemWiseSummary, setItemWiseSummary] = useState(null);
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  const [isEntriesListLoading, setIsEntriesListLoading] = useState(false);

  const limit = 50;

  useEffect(() => {
    if (activeTab === 'all-entries') {
      fetchReports();
    } else if (activeTab === 'item-wise') {
      fetchEntriesForSummary();
    }
  }, [activeTab, page, search, fromDate, toDate, transferType]);

  const fetchReports = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('skip', page * limit);
      params.append('limit', limit);
      if (search) params.append('search', search);
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);
      if (transferType && transferType !== 'all') params.append('transfer_type', transferType);

      const response = await apiClient.get(`/outward/reports?${params}`);
      setEntries(response.data.entries || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching reports:', error);
      toast.error('Failed to load reports');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchEntriesForSummary = async () => {
    setIsEntriesListLoading(true);
    try {
      const response = await apiClient.get('/outward/reports/entries-list');
      setEntriesForSummary(response.data.entries || []);
    } catch (error) {
      console.error('Error fetching entries list:', error);
      toast.error('Failed to load entries list');
    } finally {
      setIsEntriesListLoading(false);
    }
  };

  const fetchItemWiseSummary = async (entryId) => {
    if (!entryId) {
      setItemWiseSummary(null);
      return;
    }
    
    setIsSummaryLoading(true);
    try {
      const response = await apiClient.get(`/outward/reports/item-wise-summary/${entryId}`);
      setItemWiseSummary(response.data);
    } catch (error) {
      console.error('Error fetching item wise summary:', error);
      toast.error('Failed to load item wise summary');
      setItemWiseSummary(null);
    } finally {
      setIsSummaryLoading(false);
    }
  };

  const handleEntrySelect = (entryId) => {
    setSelectedEntryId(entryId);
    fetchItemWiseSummary(entryId);
  };

  const handleViewDetails = async (entry) => {
    try {
      const response = await apiClient.get(`/outward/entries/${entry.entry_id}`);
      setSelectedEntry(response.data);
      setShowDetailDialog(true);
    } catch (error) {
      console.error('Error fetching entry details:', error);
      toast.error('Failed to load entry details');
    }
  };

  const handleExportCSV = () => {
    if (entries.length === 0) {
      toast.error('No data to export');
      return;
    }

    const headers = ['Entry ID', 'Document', 'Staff ID', 'Transfer Type', 'Destination', 'Vehicle No', 'Bundles', 'Timestamp'];
    const rows = entries.map(e => [
      e.entry_id,
      e.document_barcode,
      e.staff_id,
      e.transfer_type === 'job_worker' ? 'Job Worker' : 'Internal Transfer',
      e.destination || '',
      e.vehicle_no || '',
      e.bundles?.length || 0,
      e.timestamp
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `outward_reports_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Report exported');
  };

  const handleExportItemWiseSummaryCSV = () => {
    if (!itemWiseSummary || itemWiseSummary.summary.length === 0) {
      toast.error('No data to export');
      return;
    }

    const headers = ['Barcode', 'Design Name', 'Bundle Count', 'Count of USNs'];
    const rows = itemWiseSummary.summary.map(item => [
      item.barcode,
      item.design_name || '-',
      item.bundle_count,
      item.usn_count
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `item_wise_summary_${itemWiseSummary.entry_id}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Item Wise Summary exported');
  };

  const clearFilters = () => {
    setSearch('');
    setFromDate('');
    setToDate('');
    setTransferType('all');
    setPage(0);
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      {/* Header */}
      <header className="bg-[#1A1F2E] text-[#F9FAFB] sticky top-0 z-40 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin/dashboard')}
              className="text-[#F9FAFB] hover:bg-white/10"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Outward Reports</h1>
              <p className="text-sm text-[#E8DCC8]">Job Worker & Internal Transfer Records</p>
            </div>
          </div>
          {activeTab === 'all-entries' && (
            <Button
              onClick={handleExportCSV}
              variant="outline"
              className="bg-transparent border-white/30 text-white hover:bg-white/10"
              data-testid="export-csv-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          )}
          {activeTab === 'item-wise' && itemWiseSummary && itemWiseSummary.summary.length > 0 && (
            <Button
              onClick={handleExportItemWiseSummaryCSV}
              variant="outline"
              className="bg-transparent border-white/30 text-white hover:bg-white/10"
              data-testid="export-item-wise-csv-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          )}
        </div>
      </header>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-2 mb-4">
            <TabsTrigger value="all-entries" className="flex items-center gap-2" data-testid="all-entries-tab">
              <Truck className="w-4 h-4" />
              All Entries
            </TabsTrigger>
            <TabsTrigger value="item-wise" className="flex items-center gap-2" data-testid="item-wise-tab">
              <Package className="w-4 h-4" />
              Item Wise Summary
            </TabsTrigger>
          </TabsList>

          {/* All Entries Tab Content */}
          <TabsContent value="all-entries">
            {/* Filters */}
            <div className="bg-white rounded-lg border border-[#E5DFD0] p-4 shadow-sm mb-4">
              <div className="flex flex-wrap gap-4 items-end">
                <div className="flex-1 min-w-[200px]">
                  <label className="text-sm text-[#5A5F6E] mb-1 block">Search</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8F9E]" />
                    <Input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Entry ID, Document, Staff..."
                      className="pl-10"
                      data-testid="search-input"
                    />
                  </div>
                </div>

                <div className="w-[150px]">
                  <label className="text-sm text-[#5A5F6E] mb-1 block">Transfer Type</label>
                  <Select value={transferType} onValueChange={setTransferType}>
                    <SelectTrigger data-testid="transfer-type-select">
                      <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="job_worker">Job Worker</SelectItem>
                      <SelectItem value="internal_transfer">Internal Transfer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="w-[150px]">
                  <label className="text-sm text-[#5A5F6E] mb-1 block">From Date</label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8F9E]" />
                    <Input
                      type="date"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                      className="pl-10"
                      data-testid="from-date-input"
                    />
                  </div>
                </div>

                <div className="w-[150px]">
                  <label className="text-sm text-[#5A5F6E] mb-1 block">To Date</label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8F9E]" />
                    <Input
                      type="date"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                      className="pl-10"
                      data-testid="to-date-input"
                    />
                  </div>
                </div>

                <Button
                  variant="outline"
                  onClick={clearFilters}
                  className="h-10"
                  data-testid="clear-filters-btn"
                >
                  <Filter className="w-4 h-4 mr-2" />
                  Clear
                </Button>
              </div>
            </div>

            {/* Results Table */}
            <div className="bg-white rounded-lg border border-[#E5DFD0] shadow-sm overflow-hidden">
              {isLoading ? (
                <div className="p-12 text-center text-[#8A8F9E]">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
                  Loading...
                </div>
              ) : entries.length === 0 ? (
                <div className="p-12 text-center text-[#8A8F9E]">
                  <Truck className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p className="text-lg">No outward entries found</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="entries-table">
                      <thead>
                        <tr className="bg-[#F5F1E8]">
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Entry ID</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Document</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Type</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Destination</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Vehicle</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-[#1A1F2E]">Bundles</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Date</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-[#1A1F2E]">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#E5DFD0]">
                        {entries.map((entry) => (
                          <tr key={entry.entry_id} className="hover:bg-[#FAFAF8]">
                            <td className="px-4 py-3 font-mono text-sm font-medium text-[#B8374A]">
                              {entry.entry_id}
                            </td>
                            <td className="px-4 py-3 font-mono text-sm">
                              {entry.document_barcode}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                entry.transfer_type === 'job_worker' 
                                  ? 'bg-blue-100 text-blue-700' 
                                  : 'bg-purple-100 text-purple-700'
                              }`}>
                                {entry.transfer_type === 'job_worker' ? 'Job Worker' : 'Internal'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {entry.destination || '-'}
                            </td>
                            <td className="px-4 py-3 text-sm font-mono">
                              {entry.vehicle_no || '-'}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className="bg-[#F5F1E8] px-2 py-1 rounded text-sm font-medium">
                                {entry.bundles?.length || 0}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-[#5A5F6E]">
                              {entry.timestamp ? new Date(entry.timestamp).toLocaleDateString() : '-'}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleViewDetails(entry)}
                                className="text-[#B8374A] hover:bg-[#FEF2F2]"
                                data-testid={`view-details-${entry.entry_id}`}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="flex items-center justify-between px-4 py-3 border-t border-[#E5DFD0]">
                    <p className="text-sm text-[#5A5F6E]">
                      Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.max(0, p - 1))}
                        disabled={page === 0}
                        data-testid="prev-page-btn"
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => p + 1)}
                        disabled={(page + 1) * limit >= total}
                        data-testid="next-page-btn"
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </TabsContent>

          {/* Item Wise Summary Tab Content */}
          <TabsContent value="item-wise">
            <div className="space-y-4">
              {/* Entry Selection */}
              <div className="bg-white rounded-lg border border-[#E5DFD0] p-4 shadow-sm">
                <label className="text-sm text-[#5A5F6E] mb-2 block font-medium">
                  Select Outward Entry
                </label>
                <Select 
                  value={selectedEntryId} 
                  onValueChange={handleEntrySelect}
                  disabled={isEntriesListLoading}
                >
                  <SelectTrigger className="w-full max-w-xl" data-testid="entry-select">
                    <SelectValue placeholder={isEntriesListLoading ? "Loading entries..." : "Select an outward entry"} />
                  </SelectTrigger>
                  <SelectContent>
                    {entriesForSummary.map((entry) => (
                      <SelectItem key={entry.entry_id} value={entry.entry_id}>
                        <span className="font-mono text-sm">{entry.entry_id}</span>
                        <span className="text-[#5A5F6E] ml-2">
                          - {entry.document_barcode} 
                          {entry.destination && ` (${entry.destination})`}
                          {` | ${entry.bundle_count} bundles`}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Summary Results */}
              <div className="bg-white rounded-lg border border-[#E5DFD0] shadow-sm overflow-hidden">
                {isSummaryLoading ? (
                  <div className="p-12 text-center text-[#8A8F9E]">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
                    Loading summary...
                  </div>
                ) : !selectedEntryId ? (
                  <div className="p-12 text-center text-[#8A8F9E]">
                    <FileText className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p className="text-lg">Select an outward entry to view item wise summary</p>
                  </div>
                ) : !itemWiseSummary || itemWiseSummary.summary.length === 0 ? (
                  <div className="p-12 text-center text-[#8A8F9E]">
                    <Package className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p className="text-lg">No items found for this entry</p>
                    <p className="text-sm mt-2">This entry may not have any USNs assigned to its bundles</p>
                  </div>
                ) : (
                  <>
                    {/* Summary Header */}
                    <div className="bg-[#F5F1E8] px-4 py-3 border-b border-[#E5DFD0]">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div>
                          <span className="font-mono text-sm font-medium text-[#B8374A]">
                            {itemWiseSummary.entry_id}
                          </span>
                          <span className="text-sm text-[#5A5F6E] ml-2">
                            | Document: {itemWiseSummary.document_barcode}
                          </span>
                          {itemWiseSummary.destination && (
                            <span className="text-sm text-[#5A5F6E] ml-2">
                              | Destination: {itemWiseSummary.destination}
                            </span>
                          )}
                        </div>
                        <div className="flex gap-4 text-sm">
                          <span className="bg-white px-3 py-1 rounded border">
                            <span className="text-[#5A5F6E]">Total Bundles:</span>
                            <span className="font-semibold ml-1">{itemWiseSummary.total_bundles}</span>
                          </span>
                          <span className="bg-white px-3 py-1 rounded border">
                            <span className="text-[#5A5F6E]">Total USNs:</span>
                            <span className="font-semibold ml-1">{itemWiseSummary.total_usns}</span>
                          </span>
                          <span className="bg-white px-3 py-1 rounded border">
                            <span className="text-[#5A5F6E]">Unique Items:</span>
                            <span className="font-semibold ml-1">{itemWiseSummary.unique_items}</span>
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Summary Table */}
                    <div className="overflow-x-auto">
                      <table className="w-full" data-testid="item-wise-summary-table">
                        <thead>
                          <tr className="bg-[#FAFAF8]">
                            <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Barcode</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Design Name</th>
                            <th className="px-4 py-3 text-center text-sm font-semibold text-[#1A1F2E]">Bundle Count</th>
                            <th className="px-4 py-3 text-center text-sm font-semibold text-[#1A1F2E]">Count of USNs</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-[#E5DFD0]">
                          {itemWiseSummary.summary.map((item, idx) => (
                            <tr key={idx} className="hover:bg-[#FAFAF8]">
                              <td className="px-4 py-3 font-mono text-sm font-medium text-[#1A1F2E]">
                                {item.barcode}
                              </td>
                              <td className="px-4 py-3 text-sm text-[#5A5F6E]">
                                {item.design_name || '-'}
                              </td>
                              <td className="px-4 py-3 text-center">
                                <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-sm font-medium">
                                  {item.bundle_count}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-sm font-medium">
                                  {item.usn_count}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="bg-[#F5F1E8] font-semibold">
                            <td className="px-4 py-3 text-sm" colSpan={2}>Total</td>
                            <td className="px-4 py-3 text-center text-sm">
                              {itemWiseSummary.summary.reduce((acc, item) => acc + item.bundle_count, 0)}
                            </td>
                            <td className="px-4 py-3 text-center text-sm">
                              {itemWiseSummary.total_usns}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#B8374A]">
              Outward Entry: {selectedEntry?.entry_id}
            </DialogTitle>
          </DialogHeader>
          
          {selectedEntry && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-[#5A5F6E]">Document Barcode</p>
                  <p className="font-mono font-medium">{selectedEntry.document_barcode}</p>
                </div>
                <div>
                  <p className="text-sm text-[#5A5F6E]">Staff ID</p>
                  <p className="font-medium">{selectedEntry.staff_id}</p>
                </div>
                <div>
                  <p className="text-sm text-[#5A5F6E]">Transfer Type</p>
                  <p className="font-medium">
                    {selectedEntry.transfer_type === 'job_worker' ? 'Job Worker' : 'Internal Transfer'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-[#5A5F6E]">Destination</p>
                  <p className="font-medium">{selectedEntry.destination || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-[#5A5F6E]">Vehicle No</p>
                  <p className="font-mono font-medium">{selectedEntry.vehicle_no || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-[#5A5F6E]">Timestamp</p>
                  <p className="font-medium">
                    {selectedEntry.timestamp ? new Date(selectedEntry.timestamp).toLocaleString() : '-'}
                  </p>
                </div>
              </div>

              {selectedEntry.remarks && (
                <div>
                  <p className="text-sm text-[#5A5F6E]">Remarks</p>
                  <p className="font-medium">{selectedEntry.remarks}</p>
                </div>
              )}

              <div>
                <p className="text-sm text-[#5A5F6E] mb-2">
                  Bundles ({selectedEntry.bundles?.length || 0})
                </p>
                <div className="bg-[#F5F1E8] rounded-lg p-3 max-h-40 overflow-y-auto">
                  {selectedEntry.bundles?.map((bundle, idx) => (
                    <div key={idx} className="flex justify-between py-1 text-sm">
                      <span className="font-mono">{bundle.bundle_barcode}</span>
                      <span className="text-[#5A5F6E]">
                        {bundle.timestamp ? new Date(bundle.timestamp).toLocaleTimeString() : '-'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {selectedEntry.signed_image_urls?.length > 0 && (
                <div>
                  <p className="text-sm text-[#5A5F6E] mb-2">Document Images</p>
                  <div className="grid grid-cols-3 gap-2">
                    {selectedEntry.signed_image_urls.map((url, idx) => (
                      <img
                        key={idx}
                        src={url}
                        alt={`Document ${idx + 1}`}
                        className="w-full h-24 object-cover rounded border"
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OutwardReports;
